import { useEffect, useRef } from 'react';
import { useLocation } from '@remix-run/react';
import { trackKlaviyoViewedPage } from '~/common/utils/klaviyo';

/**
 * Emits one Klaviyo `Viewed Page` per client-side navigation.
 *
 * klaviyo.js emits `Active on Site` once per *document* load, so on an SPA every route
 * change after the first was invisible to Klaviyo — a profile showed product views and cart
 * adds with no browsing path between them. This fills that gap with a separate custom
 * metric; `Active on Site` is left entirely to klaviyo.js, untouched.
 *
 * Because the metric is ours and not Klaviyo's, the **first** commit is tracked like any
 * other — there is no landing-page skip to reason about. A skip would have been needed only
 * if we were pushing `Active on Site` ourselves, and it would have been wrong three ways:
 * klaviyo.js's own event is session-scoped (so a mid-session reload would record nothing at
 * all), it fires for whichever page the visitor was on when they accepted consent rather
 * than the one they landed on, and the two forced client-side redirects
 * (`app/layout/index.tsx`'s flash-sale bounce, `app/routes/event.tsx`) change the pathname
 * anyway.
 *
 * Those redirects do mean a document opened on `/shop` during a flash sale legitimately
 * emits two events — `/shop`, then `/` — which is honest rather than a duplicate.
 *
 * Consent is enforced downstream in `pushKlaviyoEntry`, which also declines to queue this
 * metric before a decision exists. A sibling of `useKlaviyoConsent` rather than part of it:
 * that hook owns the script lifecycle (append, purge, reload), this one owns navigation.
 */
export const useKlaviyoPageTracking = () => {
  const { pathname } = useLocation();
  const lastTrackedPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastTrackedPathRef.current === pathname) return;

    // Recorded before the push, not after. StrictMode double-invokes effects in
    // development, and this is what makes the second pass a no-op instead of a duplicate
    // event — refs survive it, so the second run sees an equal pathname.
    lastTrackedPathRef.current = pathname;

    trackKlaviyoViewedPage(pathname);
  }, [pathname]);
};
